import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { StrollerProductGallery } from "@/mk2/apps/strollers/containers/StrollerProductGallery/StrollerProductGallery";
import { strollerProductGalleryFetchTrigger } from "@/mk2/apps/strollers/containers/StrollerProductGallery/StrollerProductGallery.actions";
import { strollersProductGalleryReducer } from "@/mk2/apps/strollers/containers/StrollerProductGallery/StrollerProductGallery.reducers";
import strollersProductPhotoSaga from "@/mk2/apps/strollers/containers/StrollerProductGallery/StrollerProductGallery.sagas";
import { getStrollerProductGalleryState } from "@/mk2/apps/strollers/containers/StrollerProductGallery/StrollerProductGallery.selectors";
import { strollersDetailUrl, strollersDetailVariantUrl } from "@/mk2/apps/strollers/urls";
import { AppName } from "@/mk2/containers/Application/Application";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, { HalfpageProps, PageRouteProps, RightContentSide } from "@/mk2/pages/Page";
import { AppState } from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    PhotoDetailSchema,
    StrollerEntity,
    StrollerPhoto,
    StrollerSchema,
    StrollerVariantEntity,
    StrollerVariantSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import {
    SEO_TITLE_STROLLER_REVIEWS_stroller,
    STROLLER_SEO_GENERAL_DESC,
} from "@/mk/autogenerated/translations/StrollerProductGalleryPage.cbadcbe86c4b7c668df919fd74f4fa2c";

export interface OwnProps {}

export interface StateProps {
    strollerSlug: string;
    strollerVariantId: number | null;
    loadingState: LoadingState;
    stroller: StrollerEntity;
    strollerVariants: StrollerVariantEntity[];
    strollerPhotos: StrollerPhoto[];
}

export interface DispatchProps {
    onLoad(slug: string, strollerVariantId: number | null);
}

export type RouteProps = PageRouteProps<{
    slug: string;
    variantId: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class StrollerProductGalleryPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const slug = ownProps.routeParams.slug;
            const strollerVariantId = ownProps.routeParams.variantId
                ? Number(ownProps.routeParams.variantId)
                : null;

            const photosState = getStrollerProductGalleryState(state, slug, strollerVariantId);
            const stroller = getDenormalizedEntity<StrollerEntity>(
                state,
                StrollerSchema,
                photosState.strollerId,
            );
            const strollerVariants = getDenormalizedEntities<StrollerVariantEntity>(
                state,
                StrollerVariantSchema,
                photosState.strollerVariantIds,
            );
            const strollerPhotos = getDenormalizedEntities<StrollerPhoto>(
                state,
                PhotoDetailSchema,
                photosState.strollerPhotosIds,
            );

            return {
                strollerSlug: slug,
                strollerVariantId,
                loadingState: photosState.loadingState,
                stroller,
                strollerPhotos,
                strollerVariants,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: strollerProductGalleryFetchTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        const { stroller } = props;
        return stroller
            ? interpolate(SEO_TITLE_STROLLER_REVIEWS_stroller, { stroller: stroller.name })
            : null;
    }

    public getMetaDescription(props: Props): string {
        const { stroller } = props;
        if (stroller) {
            return `${stroller.name} - ${STROLLER_SEO_GENERAL_DESC}`;
        }
    }

    public render(props: Props): false | JSX.Element {
        return <StrollerProductGallery {...props} />;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public hasMobileLogo(props: Props): boolean {
        return true;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        const { strollerSlug, strollerVariantId } = props;
        return strollerVariantId
            ? url(strollersDetailVariantUrl, { slug: strollerSlug, variantId: strollerVariantId })
            : url(strollersDetailUrl, { slug: strollerSlug });
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Strollers - Top 02 - Desktop",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer(
            "containers.strollers.strollersProductGallery",
            strollersProductGalleryReducer,
        );
        store.injectSaga("containers.strollers.strollersProductGallery", strollersProductPhotoSaga);
    }
}
